import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { appDataDetails, exprEvalConfig } from '../../../../../../../../../../shared/core/shared.selectors';
import { ExpressionEvaluatorService } from '../../../../../../../../../../shared/services/expression-evaluator.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../../shared/components';
import { Parser } from 'expr-eval';

interface FieldValue {
  id: string;
  value: any;
}

interface DataItem {
  internalId: string;
  fieldValues: FieldValue[];
}

@Component({
  standalone: false,
  selector: 'app-item-postings-custom-group-dialog',
  templateUrl: './item-postings-custom-group-dialog.component.html',
  styleUrls: ['./item-postings-custom-group-dialog.component.scss'],
})
export class ItemPostingsCustomGroupDialogComponent implements OnInit, OnDestroy {
  bidItemName = '';
  areaCalculationGroup: any = {};
  existingGroupValues: any[] = [];
  projectLookups: any = {};

  // Derived from areaCalculationGroup
  customFields: any[] = [];
  summaryFields: any[] = [];
  sections: any[] = [];
  isMultiLine = false;
  hasSectionMultiLine = false;
  dropdownField: any = null;
  isDropdownHasPickListValues = false;

  // Form state
  formData: { summaryData: FieldValue[]; data: DataItem[] } = { summaryData: [], data: [] };
  summaryFormGroup: FormGroup = new FormGroup({});
  dataFormGroups: { [internalId: string]: FormGroup } = {};

  // Computed values
  customFieldValuesInfo: { [internalId: string]: { [fieldId: string]: FieldValue } } = {};
  summaryFieldValuesInfo: { [fieldId: string]: FieldValue } = {};
  totalDataInfo: { [name: string]: number } = {};
  filteredCustomFieldsDictByShape: { [internalId: string]: any[] } = {};

  // Caches (avoids returning new array references on every change detection cycle)
  pickListOptionsCache: { [fieldId: string]: any[] } = {};
  sectionFieldsCache: { [sectionName: string]: any[] } = {};

  // Accordion state
  expandedDataItemId: string = null;
  expandedSectionNames: { [internalId: string]: string } = {};

  // Store data
  private appData: any = {};
  private exprEvalConfigData: any = {};

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public dialogRef: MatDialogRef<ItemPostingsCustomGroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<fromRoot.State>,
    private dialog: MatDialog,
    private exprEvalService: ExpressionEvaluatorService,
  ) {
    this.bidItemName = data?.bidItemName || '';
    this.areaCalculationGroup = data?.areaCalculationGroup || {};
    this.existingGroupValues = data?.existingGroupValues || [];
    this.projectLookups = data?.projectLookups || {};
  }

  ngOnInit() {
    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(appData => {
        this.appData = appData;
      });

    this.store.select(exprEvalConfig)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(config => {
        this.exprEvalConfigData = config;
      });

    // Initialize only once
    this.initializeComponent();
  }

  private initializeComponent() {
    const group = this.areaCalculationGroup;

    // Derive fields from group definition and add componentType (same as effects do for control_forms)
    this.customFields = [...(group.fields || [])]
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map(field => this.processField(field));
    this.summaryFields = [...(group.ui_config?.summary_fields || [])].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    this.sections = [...(group.ui_config?.sections || [])].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    this.isMultiLine = group.ui_config?.multi_line || false;
    this.hasSectionMultiLine = this.sections.some(s => s.multi_line);

    // First field with pick_list_values is the dropdown field
    this.dropdownField = this.customFields.find(f => f.componentType === 'dropdown') || null;
    this.isDropdownHasPickListValues = !!this.dropdownField?.ui_config?.pick_list_values?.length;

    // Generate form data from existing group_values
    this.generateFormData();

    // Build reactive form controls
    this.buildFormControls();

    // Run initial calculation
    this.recalculateAll();
  }

  // --- Form Data Generation (from mobile generateBidItemAreaCalculationFormData) ---

  private generateFormData() {
    const context = this.areaCalculationGroup.context;
    const existingGroup = this.existingGroupValues.find(gv => gv?.context === context);

    if (existingGroup) {
      // Summary data
      this.formData.summaryData = (existingGroup.summary_field_values || []).map(fv => ({ ...fv }));

      // Field values
      if (this.isMultiLine) {
        const multiLineValues = existingGroup.multi_line_field_values || [];
        this.formData.data = multiLineValues.map(fieldValues => ({
          internalId: uuidv4(),
          fieldValues: this.normalizeFieldValues(fieldValues),
        }));
      } else if (this.hasSectionMultiLine) {
        // Section multi-line: field_values is a 2D array
        const fieldValues2D = existingGroup.field_values || [];
        if (fieldValues2D.length > 0) {
          const rootValues = this.normalizeFieldValues(fieldValues2D[0] || []);
          const dataItem: DataItem = { internalId: uuidv4(), fieldValues: rootValues };
          this.formData.data = [dataItem];

          // Remaining rows stored as additional data items for multi-line sections
          for (let i = 1; i < fieldValues2D.length; i++) {
            this.formData.data.push({
              internalId: uuidv4(),
              fieldValues: this.normalizeFieldValues(fieldValues2D[i] || []),
            });
          }
        }
      } else {
        // Single-line
        const fieldValues = existingGroup.field_values || [];
        this.formData.data = [{
          internalId: uuidv4(),
          fieldValues: this.normalizeFieldValues(fieldValues),
        }];
      }
    } else {
      // No existing data — create one empty item
      this.formData.data = [{ internalId: uuidv4(), fieldValues: [] }];
    }

    // Auto-expand first data item
    if (this.formData.data.length > 0) {
      this.expandedDataItemId = this.formData.data[0].internalId;
    }
  }

  private normalizeFieldValues(fieldValues: any): FieldValue[] {
    if (Array.isArray(fieldValues)) {
      return fieldValues.map(fv => {
        if (fv && typeof fv === 'object' && fv.id !== undefined) {
          return { id: fv.id, value: fv.value };
        }
        return fv;
      }).filter(fv => fv?.id != null);
    }
    // Handle object-indexed format { "0": {id, value}, "1": {...} }
    if (fieldValues && typeof fieldValues === 'object') {
      return Object.keys(fieldValues)
        .filter(key => !isNaN(Number(key)))
        .map(key => fieldValues[key])
        .filter(fv => fv?.id != null)
        .map(fv => ({ id: fv.id, value: fv.value }));
    }
    return [];
  }

  // --- Build Form Controls ---

  private buildFormControls() {
    // Summary form group
    const summaryControls: { [key: string]: FormControl } = {};
    this.summaryFields.forEach(field => {
      const existing = this.formData.summaryData.find(fv => fv.id === field.id);
      summaryControls[field.id] = new FormControl(existing?.value ?? null);
    });
    this.summaryFormGroup = new FormGroup(summaryControls);

    // Data item form groups
    this.dataFormGroups = {};
    this.formData.data.forEach(dataItem => {
      this.buildDataItemFormGroup(dataItem);
    });
  }

  private buildDataItemFormGroup(dataItem: DataItem) {
    const controls: { [key: string]: FormControl } = {};
    this.customFields.forEach(field => {
      const existing = dataItem.fieldValues.find(fv => fv.id === field.id);
      controls[field.id] = new FormControl(existing?.value ?? null);

      // Time fields need extra controls for timePeriod and amPm
      if (this.getFieldType(field) === 'time') {
        const timeValue = existing?.value || '';
        const { timePeriod, amPm, display } = this.parseTimeValue(timeValue);
        if (display) {
          controls[field.id] = new FormControl(display);
        }
        controls[`timePeriod_${field.id}`] = new FormControl(timePeriod);
        controls[`amPm_${field.id}`] = new FormControl(amPm);
      }
    });
    this.dataFormGroups[dataItem.internalId] = new FormGroup(controls);
  }

  // --- Field Change Handlers ---

  onFieldChange(dataItem: DataItem, field: any, value: any) {
    let resolvedValue;
    if (value && typeof value === 'object' && typeof value.toISOString === 'function') {
      // Moment or Date object (from mat-datepicker)
      resolvedValue = value.toISOString();
    } else if (value && typeof value === 'object') {
      resolvedValue = value.value;
    } else {
      resolvedValue = value;
    }

    // Update formData
    const fvIndex = dataItem.fieldValues.findIndex(fv => fv.id === field.id);
    if (fvIndex !== -1) {
      dataItem.fieldValues[fvIndex] = { id: field.id, value: resolvedValue };
    } else {
      dataItem.fieldValues.push({ id: field.id, value: resolvedValue });
    }

    // Handle dependent fields
    this.applyDependentFields(dataItem, field, resolvedValue);

    // Clear field values when dropdown shape is cleared
    if (this.isDropdownHasPickListValues && field.id === this.dropdownField?.id && resolvedValue == null) {
      dataItem.fieldValues = [];
      // Reset all form controls for this data item
      const fg = this.dataFormGroups[dataItem.internalId];
      if (fg) Object.values(fg.controls).forEach(ctrl => ctrl.setValue(null, { emitEvent: false }));
    }

    this.recalculateAll();
  }

  onSummaryFieldChange(field: any, value: any) {
    const fvIndex = this.formData.summaryData.findIndex(fv => fv.id === field.id);
    if (fvIndex !== -1) {
      this.formData.summaryData[fvIndex] = { id: field.id, value };
    } else {
      this.formData.summaryData.push({ id: field.id, value });
    }
    this.recalculateAll();
  }

  private applyDependentFields(dataItem: DataItem, field: any, value: any) {
    const dependentFields = field.ui_config?.dependent_fields;
    if (!dependentFields?.length || value == null) return;

    const pickListPath = field.ui_config?.pick_list || '';
    const pickListType = pickListPath.split('.')?.[0];
    if (!pickListType) return;

    // Try to find lookups from project first, then appData.lookups
    const projectLookupData = this.projectLookups?.[pickListType] || [];
    const appDataLookupData = this.appData?.lookups?.[pickListType] || [];
    const lookups = projectLookupData.length > 0 ? projectLookupData : appDataLookupData;
    const selectedLookup = lookups.find(l => l.value === value);
    if (!selectedLookup?.extra_attributes) return;

    dependentFields.forEach(dep => {
      const targetField = this.customFields.find(cf => cf.name === dep.name);
      if (!targetField) return;

      const extraValue = selectedLookup.extra_attributes[dep.extra_attribute] ?? null;

      // Update formData
      const idx = dataItem.fieldValues.findIndex(fv => fv.id === targetField.id);
      if (idx !== -1) {
        dataItem.fieldValues[idx] = { id: targetField.id, value: extraValue };
      } else {
        dataItem.fieldValues.push({ id: targetField.id, value: extraValue });
      }

      // Update form control
      this.dataFormGroups[dataItem.internalId]?.controls[targetField.id]?.setValue(extraValue, { emitEvent: false });
    });
  }

  // --- Expression Evaluation (from mobile generateBidItemAreaCalculationFieldsInfo) ---

  private recalculateAll() {
    this.totalDataInfo = {};
    this.customFieldValuesInfo = {};

    // Filter visible fields per data item
    this.updateFilteredFieldsByShape();

    // Evaluate custom field expressions for each data item
    for (const dataItem of this.formData.data) {
      const filteredFields = this.isDropdownHasPickListValues
        ? (this.filteredCustomFieldsDictByShape[dataItem.internalId] || [])
        : this.customFields;

      const fieldValuesDictByName = this.getFieldValuesDictByName(dataItem.fieldValues, filteredFields, dataItem);
      const dataItemFieldValuesInfo: { [fieldId: string]: FieldValue } = {};

      for (const field of filteredFields) {
        const { info, newTotalDataInfo } = this.evaluateCustomField(field, fieldValuesDictByName, dataItem.fieldValues);
        if (info?.id) {
          dataItemFieldValuesInfo[field.id] = info;

          // Update form control for computed fields
          if (field.ui_config?.expression || field.ui_config?.expression_name) {
            this.dataFormGroups[dataItem.internalId]?.controls[field.id]?.setValue(info.value, { emitEvent: false });
          }
        }
        Object.assign(this.totalDataInfo, newTotalDataInfo);
      }

      this.customFieldValuesInfo[dataItem.internalId] = dataItemFieldValuesInfo;
    }

    // Evaluate summary fields
    this.summaryFieldValuesInfo = this.computeSummaryFieldValues();

    // Update summary form controls for computed summary fields
    this.summaryFields.forEach(field => {
      const computed = this.summaryFieldValuesInfo[field.id];
      if (computed && (field.rollup_field || field.expression_name)) {
        this.summaryFormGroup.controls[field.id]?.setValue(computed.value, { emitEvent: false });
      }
    });
  }

  private getFieldValuesDictByName(fieldValues: FieldValue[], fields: any[], dataItem?: DataItem): { [name: string]: any } {
    const dict: { [name: string]: any } = {};

    // First populate from all fields using form control values as fallback
    if (dataItem) {
      const formGroup = this.dataFormGroups[dataItem.internalId];
      for (const field of fields) {
        if (!field.name) continue;
        const fv = fieldValues.find(f => f.id === field.id);
        let value = fv?.value ?? formGroup?.controls[field.id]?.value ?? null;

        // Default null number fields to 0 for expression evaluation
        if (value == null && field.type === 'number') {
          value = 0;
        }
        // Convert string numbers to actual numbers for math
        if (field.type === 'number' && typeof value === 'string') {
          const parsed = parseFloat(value);
          value = isNaN(parsed) ? 0 : parsed;
        }

        dict[field.name] = value;
      }
    } else {
      for (const fv of fieldValues) {
        const field = fields.find(f => f.id === fv.id);
        if (field?.name) {
          dict[field.name] = fv.value;
        }
      }
    }

    return dict;
  }

  private evaluateCustomField(field: any, fieldValuesDictByName: { [name: string]: any }, fieldValues: FieldValue[])
    : { info: FieldValue | null; newTotalDataInfo: { [name: string]: number } } {
    const newTotalDataInfo = { ...this.totalDataInfo };
    let info: FieldValue | null = null;

    const expressionName = field.ui_config?.expression_name || field.ui_config?.value_expression || null;
    const directExpression = field.ui_config?.expression || null;

    if (!expressionName && !directExpression) {
      // Use existing value from fieldValues, fallback to fieldValuesDictByName
      const existing = fieldValues.find(fv => fv.id === field.id);
      const value = existing?.value ?? fieldValuesDictByName[field.name] ?? null;
      if (value != null) {
        info = { id: field.id, value };
      }
    } else if (directExpression) {
      // Direct expression evaluation
      try {
        const result = this.exprEvalService.evaluateExpression(directExpression, fieldValuesDictByName);
        if (result != null) {
          info = { id: field.id, value: result };
        }
      } catch (e) {
        // expression evaluation failed
      }
    } else if (expressionName) {
      // Server-defined expression
      const result = this.evaluateServerExpression(expressionName, fieldValuesDictByName);
      if (result != null) {
        info = { id: field.id, value: result };
      }
    }

    // Accumulate totalDataInfo for numeric fields (for rollup summary)
    if (field.type === 'number' && field.name) {
      const oldValue = parseFloat(newTotalDataInfo[field.name] as any) || 0;
      const currentValue = parseFloat(info?.value) || 0;
      newTotalDataInfo[field.name] = oldValue + currentValue;
    }

    return { info, newTotalDataInfo };
  }

  private evaluateServerExpression(expressionKey: string, variables: { [name: string]: any }): number | null {
    const expressions = this.exprEvalConfigData?.expressions || {};
    const expression = expressions[expressionKey];
    if (!expression) return null;

    const finalVars = { PI: Math.PI, ...variables };

    // Use expr-eval Parser (same as mobile) — supports ternary, string comparisons
    try {
      const parser = new Parser();
      const parsed = parser.parse(expression);
      const result = parsed.evaluate({
        ...finalVars,
        common_round: (x: number) => Math.round(x * 100) / 100,
      });
      return typeof result === 'number' ? result : Number(result) || null;
    } catch {
      // Fallback to mathjs for simple numeric expressions
      try {
        return this.exprEvalService.evaluateExpression(expression, finalVars);
      } catch {
        return null;
      }
    }
  }

  private computeSummaryFieldValues(): { [fieldId: string]: FieldValue } {
    const info: { [fieldId: string]: FieldValue } = {};

    // Build fieldValuesDictByName for summary fields (for expression evaluation)
    const fieldValuesDictByName: { [name: string]: any } = {};
    this.summaryFields.forEach(sf => {
      const rollupField = sf.rollup_field || '';
      const exprEvalExpression = sf.expression_name || null;
      const existingFv = this.formData.summaryData.find(fv => fv.id === sf.id);

      if (!exprEvalExpression) {
        if (existingFv) {
          fieldValuesDictByName[sf.name] = existingFv.value;
        }
        if (rollupField) {
          fieldValuesDictByName[sf.name] = this.totalDataInfo[rollupField] ?? null;
        }
      }
    });

    for (const field of this.summaryFields) {
      const rollupField = field.rollup_field || '';
      const exprEvalExpression = field.expression_name || null;
      const precision = field.precision ?? null;
      const conversionFactor = this.normalizeConversionFactor(field.conversion_factor ?? 1);
      let value = null;

      if (rollupField || exprEvalExpression) {
        value = rollupField
          ? (this.totalDataInfo[rollupField] ?? null)
          : this.evaluateServerExpression(exprEvalExpression, fieldValuesDictByName);

        if (value != null && precision != null) {
          value = this.roundValue(value, precision);
        }

        if (value != null && conversionFactor != null) {
          const numericValue = Number(value);
          if (!isNaN(numericValue)) {
            value = this.roundValue(numericValue * conversionFactor, precision ?? 2);
          }
        }
      } else {
        const existing = this.formData.summaryData.find(fv => fv.id === field.id);
        if (existing) {
          info[field.id] = existing;
          continue;
        }
      }

      if (value != null) {
        info[field.id] = { id: field.id, value };
      }
    }

    return info;
  }

  // --- Display Conditions (from mobile getFilteredCustomFieldsForAShape) ---

  private updateFilteredFieldsByShape() {
    this.filteredCustomFieldsDictByShape = {};

    if (!this.isDropdownHasPickListValues) return;

    const otherFields = this.customFields.filter(f => f.id !== this.dropdownField?.id);

    for (const dataItem of this.formData.data) {
      const dropdownValue = dataItem.fieldValues.find(fv => fv.id === this.dropdownField?.id);
      const type = dropdownValue?.value ?? null;
      const visibleFields: any[] = [];

      for (const field of otherFields) {
        let expression = field.ui_config?.display_condition || '';
        if (!expression) {
          visibleFields.push(field);
          continue;
        }

        // Normalize for mathjs: replace || with or, == with ==
        expression = expression.replace(/\|\|/g, ' or ').replace(/&&/g, ' and ');

        try {
          const parser = new Parser();
          const result = parser.evaluate(expression, { type, null: null });
          if (result) {
            visibleFields.push(field);
          }
        } catch { /* skip field */ }
      }

      this.filteredCustomFieldsDictByShape[dataItem.internalId] = [this.dropdownField, ...visibleFields];
    }
  }

  // --- Multi-line: Add / Delete rows ---

  addDataItem(shapeValue?: string) {
    const newItem: DataItem = { internalId: uuidv4(), fieldValues: [] };
    if (shapeValue && this.dropdownField) {
      newItem.fieldValues.push({ id: this.dropdownField.id, value: shapeValue });
    }
    this.formData.data.push(newItem);
    this.buildDataItemFormGroup(newItem);
    if (shapeValue && this.dropdownField) {
      this.dataFormGroups[newItem.internalId]?.controls[this.dropdownField.id]?.setValue(shapeValue, { emitEvent: false });
    }
    this.expandedDataItemId = newItem.internalId;
    this.recalculateAll();
  }

  deleteDataItem(dataItem: DataItem) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Delete', 'Are you sure you want to delete?'),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.formData.data = this.formData.data.filter(d => d.internalId !== dataItem.internalId);
        delete this.dataFormGroups[dataItem.internalId];
        if (this.expandedDataItemId === dataItem.internalId) {
          this.expandedDataItemId = null;
        }
        this.recalculateAll();
      }
    });
  }

  // --- Accordion ---

  toggleDataItem(internalId: string) {
    this.expandedDataItemId = this.expandedDataItemId === internalId ? null : internalId;
  }

  toggleSection(internalId: string, sectionName: string) {
    this.expandedSectionNames[internalId] =
      this.expandedSectionNames[internalId] === sectionName ? null : sectionName;
  }

  isSectionExpanded(internalId: string, sectionName: string): boolean {
    return this.expandedSectionNames[internalId] === sectionName;
  }

  // --- Field Type Helpers ---

  getFieldType(field: any): string {
    if (field.ui_config?.display_only || field.display_only) return 'display_only';
    if (field.ui_config?.expression || field.ui_config?.expression_name || field.ui_config?.value_expression) return 'display_only';
    if (field.componentType === 'dropdown') return 'dropdown';
    if (field.componentType === 'date') return 'date';
    if (field.componentType === 'time') return 'time';
    if (field.componentType === 'number') return 'number';
    if (field.componentType === 'boolean') return 'boolean';
    if (field.componentType) return field.componentType;
    // Fallback for fields without componentType
    if (field.type === 'date') return 'date';
    if (field.type === 'time') return 'time';
    if (field.type === 'number') return 'number';
    if (field.type === 'boolean') return 'boolean';
    return 'text';
  }

  getSummaryFieldType(field: any): string {
    if (field.rollup_field || field.expression_name) return 'display_only';
    if (field.type === 'boolean') return 'boolean';
    if (field.type === 'number') return 'number';
    return 'text';
  }

  getPickListOptions(field: any): any[] {
    // Return cached options if available
    if (this.pickListOptionsCache[field.id]) {
      return this.pickListOptionsCache[field.id];
    }

    let options: any[] = [];

    // If pick_list_values exists (inline options, already transformed to {label, value})
    if (Array.isArray(field.ui_config?.pick_list_values) && field.ui_config.pick_list_values.length > 0) {
      options = field.ui_config.pick_list_values.map(item => ({
        value: typeof item === 'string' ? item : (item.value ?? item),
        label: typeof item === 'string' ? item : (item.label || item.name || item.value),
      }));
    } else {
      // If pick_list is a string reference (e.g. "truck_id.value"), resolve from lookups
      const pickList = field.ui_config?.pick_list || field.pick_list;
      if (typeof pickList === 'string') {
        const lookupType = pickList.split('.')?.[0];
        const projectLookupData = this.projectLookups?.[lookupType] || [];
        const appDataLookupData = this.appData?.lookups?.[lookupType] || [];
        const lookupData = projectLookupData.length > 0 ? projectLookupData : appDataLookupData;
        options = lookupData.map(item => ({
          value: item.value ?? item.id ?? item,
          label: item.label || item.name || item.value || String(item),
        }));
      }
    }

    this.pickListOptionsCache[field.id] = options;
    return options;
  }

  // --- Section Helpers ---

  getFieldSpacing(field: any): string {
    const type = this.getFieldType(field);
    if (type === 'display_only') return 'mb-[30px]';
    if (type === 'boolean') return 'mb-[40px]';
    return 'mb-[20px]';
  }

  getSummaryFieldSpacing(field: any): string {
    const type = this.getSummaryFieldType(field);
    if (type === 'display_only') return 'mb-[30px]';
    if (type === 'boolean') return 'mb-[40px]';
    return 'mb-[20px]';
  }

  isFullWidthField(field: any): boolean {
    return field.ui_config?.single_column === true || field.type === 'boolean';
  }

  getSortedSections(): any[] {
    return this.sections;
  }

  getSingleLineSections(): any[] {
    return this.sections.filter(s => !s.multi_line);
  }

  getMultiLineSections(): any[] {
    return this.sections.filter(s => s.multi_line);
  }

  // For hasSectionMultiLine: add a new row for a multi-line section
  addMultiLineSectionRow(section: any) {
    const newItem: DataItem = { internalId: uuidv4(), fieldValues: [] };
    this.formData.data.push(newItem);
    this.buildDataItemFormGroup(newItem);
    this.expandedDataItemId = newItem.internalId;
    this.recalculateAll();
  }

  // For hasSectionMultiLine: delete a row from a multi-line section
  canDeleteMultiLineSectionRow(): boolean {
    // Can't delete if only one row remains (data[0] holds the only density row)
    return this.formData.data.length > 1;
  }

  deleteMultiLineSectionRow(dataItem: DataItem) {
    if (!this.canDeleteMultiLineSectionRow()) return;

    const isFirstItem = this.formData.data.indexOf(dataItem) === 0;

    // For the first item, clear the multi-line section field values instead of deleting the data item
    if (isFirstItem) {
      const multiLineSections = this.getMultiLineSections();
      const multiLineFieldIds = new Set(
        this.customFields
          .filter(f => multiLineSections.some(s => s.name === f.ui_config?.section_name))
          .map(f => f.id)
      );
      dataItem.fieldValues = dataItem.fieldValues.filter(fv => !multiLineFieldIds.has(fv.id));
      const fg = this.dataFormGroups[dataItem.internalId];
      if (fg) {
        multiLineFieldIds.forEach(id => fg.controls[id]?.setValue(null, { emitEvent: false }));
      }
      this.recalculateAll();
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Delete', 'Are you sure you want to delete?'),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.formData.data = this.formData.data.filter(d => d.internalId !== dataItem.internalId);
        delete this.dataFormGroups[dataItem.internalId];
        if (this.expandedDataItemId === dataItem.internalId) {
          this.expandedDataItemId = null;
        }
        this.recalculateAll();
      }
    });
  }

  // Get multi-line section data items
  // The first data item holds both single-line fields AND the first multi-line row
  // Additional rows are in data[1..n]
  getMultiLineSectionDataItems(): DataItem[] {
    // All data items participate — data[0] has the first density row mixed in,
    // data[1..n] are additional rows
    return this.formData.data;
  }

  // Get the title for a multi-line section row
  getMultiLineSectionRowTitle(dataItem: DataItem, section: any): string {
    const sectionFields = this.getSectionFields(section.name);
    if (sectionFields.length > 0) {
      const fv = dataItem.fieldValues.find(f => f.id === sectionFields[0].id);
      if (fv?.value != null && fv.value !== '') {
        const val = fv.value;
        if (typeof val === 'object') return val.label || val.name || val.value || 'New';
        return String(val);
      }
    }
    return 'New';
  }

  getSectionFields(sectionName: string): any[] {
    if (this.sectionFieldsCache[sectionName]) {
      return this.sectionFieldsCache[sectionName];
    }
    const fields = this.customFields
      .filter(f => f.ui_config?.section_name === sectionName)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    this.sectionFieldsCache[sectionName] = fields;
    return fields;
  }

  getVisibleFieldsForDataItem(dataItem: DataItem): any[] {
    if (this.isDropdownHasPickListValues) {
      return this.filteredCustomFieldsDictByShape[dataItem.internalId] || [];
    }
    return this.customFields;
  }

  getFieldValue(dataItem: DataItem, fieldId: string): any {
    // Check computed values first
    const computed = this.customFieldValuesInfo[dataItem.internalId]?.[fieldId];
    if (computed) return computed.value;
    // Then form control
    return this.dataFormGroups[dataItem.internalId]?.controls[fieldId]?.value;
  }

  getSummaryFieldValue(fieldId: string): any {
    const computed = this.summaryFieldValuesInfo[fieldId];
    if (computed) return computed.value;
    return this.summaryFormGroup.controls[fieldId]?.value;
  }

  // --- Accordion Header Content ---

  getDataItemTitle(dataItem: DataItem): string {
    if (this.dropdownField) {
      const fv = dataItem.fieldValues.find(f => f.id === this.dropdownField.id);
      return this.formatFieldValue(fv?.value, this.dropdownField) || this.dropdownField.label || 'Shape';
    }
    const firstField = this.customFields[0];
    if (firstField) {
      const fv = dataItem.fieldValues.find(f => f.id === firstField.id);
      return this.formatFieldValue(fv?.value, firstField) || firstField.label || '';
    }
    return '';
  }

  private formatFieldValue(value: any, field: any): string {
    if (value == null || value === '') return '';
    // Format date fields
    if (field?.type === 'date' || this.isDateString(value)) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
      }
    }
    return String(value);
  }

  private isDateString(value: any): boolean {
    if (typeof value !== 'string') return false;
    return /^\d{4}-\d{2}-\d{2}T/.test(value);
  }

  getDataItemDescription(dataItem: DataItem): string {
    const fields = this.isDropdownHasPickListValues
      ? (this.filteredCustomFieldsDictByShape[dataItem.internalId] || []).filter(f => f.id !== this.dropdownField?.id)
      : this.customFields.slice(1);

    // Find computed/display-only field (expression_name, expression, or display_only)
    const computedField = fields.find(f =>
      f.ui_config?.display_only || f.ui_config?.expression || f.ui_config?.expression_name || f.ui_config?.value_expression
    );
    if (computedField) {
      const value = this.customFieldValuesInfo[dataItem.internalId]?.[computedField.id]?.value;
      if (value != null) return `( ${computedField.label || computedField.name}: ${value} )`;
    }
    return '';
  }

  // --- Done / Delete ---

  done() {
    const context = this.areaCalculationGroup.context;
    const hasData = this.checkDataHasFieldValues();

    // Build group_values entry
    const summaryFieldValues = this.formData.summaryData
      .filter(fv => fv.value != null && fv.value !== '')
      .map(fv => ({ id: fv.id, value: fv.value }));

    // Include computed summary values
    Object.entries(this.summaryFieldValuesInfo).forEach(([fieldId, fv]) => {
      if (!summaryFieldValues.find(s => s.id === fieldId) && fv.value != null) {
        summaryFieldValues.push({ id: fv.id, value: fv.value });
      }
    });

    let fieldValuesPayload: any;

    if (this.hasSectionMultiLine) {
      // 2D array for section multi-line
      fieldValuesPayload = this.formData.data.flatMap(di =>
        di.fieldValues.length > 0
          ? [di.fieldValues.map(fv => ({ id: fv.id, value: fv.value }))]
          : []
      );
    } else if (this.isMultiLine) {
      // multi_line_field_values
      fieldValuesPayload = this.formData.data.map(di =>
        di.fieldValues.map(fv => ({ id: fv.id, value: fv.value }))
      );
    } else {
      // Single-line: flat array
      fieldValuesPayload = this.formData.data[0]?.fieldValues.map(fv => ({ id: fv.id, value: fv.value })) || [];
    }

    const groupValueEntry: any = {
      context: this.areaCalculationGroup.context,
      label: this.areaCalculationGroup.label,
      summary_field_values: summaryFieldValues,
    };

    if (this.isMultiLine && !this.hasSectionMultiLine) {
      groupValueEntry.multi_line_field_values = fieldValuesPayload;
    } else {
      groupValueEntry.field_values = fieldValuesPayload;
    }

    // Build updated group_values array
    let updatedGroupValues = [...(this.existingGroupValues || [])];
    const existingIndex = updatedGroupValues.findIndex(gv => gv?.context === context);

    if (hasData) {
      if (existingIndex !== -1) {
        updatedGroupValues[existingIndex] = { ...updatedGroupValues[existingIndex], ...groupValueEntry };
      } else {
        updatedGroupValues.push(groupValueEntry);
      }
    } else {
      if (existingIndex !== -1) {
        updatedGroupValues = updatedGroupValues.filter((_, i) => i !== existingIndex);
      }
    }

    // Check quantity auto-update
    const quantityUpdate = this.getQuantityUpdateFromSummary();

    this.dialogRef.close({
      groupValues: updatedGroupValues,
      quantityUpdate,
    });
  }

  private getQuantityUpdateFromSummary(): { shouldUpdate: boolean; value: number | null } {
    let booleanField = null;
    let firstField = null;

    this.summaryFields.forEach((sf, index) => {
      if (sf.type === 'boolean') booleanField = sf;
      if (index === 0) firstField = sf;
    });

    if (!booleanField || !firstField) return { shouldUpdate: false, value: null };

    const boolValue = this.summaryFieldValuesInfo[booleanField.id]?.value ?? this.summaryFormGroup.controls[booleanField.id]?.value;
    const firstValue = this.summaryFieldValuesInfo[firstField.id]?.value ?? null;

    if (boolValue && firstValue != null) {
      return { shouldUpdate: true, value: this.roundValue(firstValue, 2) };
    }

    return { shouldUpdate: false, value: null };
  }

  deleteAreaCalculation() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Warning',
        'The area calculation data will be removed if you proceed. Do you want to continue?',
        'Continue',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const context = this.areaCalculationGroup.context;
        const updatedGroupValues = (this.existingGroupValues || []).filter(gv => gv?.context !== context);
        this.dialogRef.close({ groupValues: updatedGroupValues, deleted: true });
      }
    });
  }

  hasExistingData(): boolean {
    return this.existingGroupValues.some(gv => gv?.context === this.areaCalculationGroup.context);
  }

  private checkDataHasFieldValues(): boolean {
    return this.formData.data.some(di => di.fieldValues.some(fv => fv.value != null && fv.value !== ''));
  }

  close() {
    this.dialogRef.close();
  }

  // --- Utility ---

  private normalizeConversionFactor(factor: any): number | null {
    if (factor == null) return null;
    if (typeof factor === 'number' && !isNaN(factor)) return factor;
    if (typeof factor === 'string' && factor.includes('/')) {
      const [num, den] = factor.split('/').map(Number);
      if (!isNaN(num) && !isNaN(den) && den !== 0) return num / den;
      return null;
    }
    const parsed = Number(factor);
    return isNaN(parsed) ? null : parsed;
  }

  private processField(field: any): any {
    let componentType: string;

    if (field?.ui_config?.pick_list || field?.ui_config?.pick_list_values?.length || field?.pick_list || field?.multi_select) {
      componentType = 'dropdown';
    } else {
      componentType = field?.type;
    }

    // Transform pick_list_values from string[] to {label, value}[]
    const processed = { ...field, componentType };
    if (Array.isArray(processed.ui_config?.pick_list_values)) {
      processed.ui_config = {
        ...processed.ui_config,
        pick_list_values: processed.ui_config.pick_list_values.map(plv =>
          typeof plv === 'string' ? { label: plv, value: plv } : plv
        ),
      };
    }

    return processed;
  }

  private parseTimeValue(value: string): { timePeriod: string | null; amPm: string | null; display: string | null } {
    if (!value) return { timePeriod: null, amPm: null, display: null };

    // Already in "HH:MM AM/PM" format
    const amPmMatch = value.match(/^(\d{1,2}:\d{2})\s*(AM|PM)$/i);
    if (amPmMatch) {
      return { timePeriod: amPmMatch[1], amPm: amPmMatch[2].toUpperCase(), display: `${amPmMatch[1]} ${amPmMatch[2].toUpperCase()}` };
    }

    // 24-hour format "HH:MM" or "HH:MM:SS" — convert to 12-hour
    const match24 = value.match(/^(\d{1,2}):(\d{2})/);
    if (match24) {
      let hours = parseInt(match24[1], 10);
      const minutes = match24[2];
      const amPm = hours >= 12 ? 'PM' : 'AM';
      if (hours === 0) hours = 12;
      else if (hours > 12) hours -= 12;
      const timePeriod = `${String(hours).padStart(2, '0')}:${minutes}`;
      return { timePeriod, amPm, display: `${timePeriod} ${amPm}` };
    }

    return { timePeriod: null, amPm: null, display: value };
  }

  private roundValue(value: number, precision: number): number {
    const factor = Math.pow(10, precision);
    return Math.round(value * factor) / factor;
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
